<!--
  Preloading strategy:
    - preference for KISSed unique small spinner for breadcrumber only
      rather than preloader placeholders
-->
<template>
  <div
    :class="[
      'page-list',
      'page-list--company-leave-request',
    ]"
  >
    <layout-teleport-to-breadcrumb>
      <layout-breadcrumb-loader
        :predicate="fetchState.status.value === 'success'"
      />
    </layout-teleport-to-breadcrumb>

    <transition
      v-if="fetchState.status.value === 'success'"
      appear
      appear-active-class="animate__animated animate__fadeIn ease-in-out a1"
    >
      <div class="page-list__body">

        <remote-work-filters-component
          class="page-list__filters"
          v-bind="{
            filters: remoteWorkFilters,
            updateFiltersCallback: updateRemoteWorkFiltersCallback,
            serviceList,
            parametersModule,
          }"
        />

        <pending-list
          ref="pendingList"
          v-bind="{
            anyFilterSet: anyRemoteWorkFilterSet,
            withBusyState: withPendingBusyState,
            listCallback: searchPendingCallback,
            approveCallback,
            rejectCallback,
            approveMultipleCallback,
            singleAcceptCallback,
            singleRejectCallback,
            singleCancelCallback,
            parametersModule,
          }"
          :default-data="defaultPendingData"
          :class="[
            'page-list__section',
            'page-list__section--pending-list'
          ]"
        >
          <template #actions>
            <ui-tooltip-wrapper
              :status="hasACL(ACLS.REMOTE_WORK_REQUEST_CREATE) ?
                UI.STATUS.DISABLED :
                UI.STATUS.ENABLED
              "
              v-bind="{
                title: `Vous n'avez pas les droits pour effectuer cette action`,
                variant: UI.VARIANT.LIGHT,
              }"
            >
              <layout-cta-sub
                component="ui-button"
                :status="
                  hasACL(ACLS.REMOTE_WORK_REQUEST_CREATE) ?
                    UI.STATUS.ENABLED :
                    UI.STATUS.DISABLED
                "
                :variant="UI.VARIANT.PRIMARY"
                :size="UI.SIZE.SMALL"
                @click="() => showRemoteWorkCreationSidebar = true"
              >
                <ui-icon
                  name="add"
                />
                &nbsp;
                Nouvelle demande
              </layout-cta-sub>
            </ui-tooltip-wrapper>
          </template>
        </pending-list>

        <archived-list
          ref="archivedList"
          v-bind="{
            cancelCallback,
            anyFilterSet: anyRemoteWorkFilterSet,
            withBusyState: withArchivedBusyState,
            listCallback: searchArchivedCallback,
            singleAcceptCallback,
            singleRejectCallback,
            singleCancelCallback,
            parametersModule,
          }"
          :default-data="defaultArchivedData"
          :class="[
            'page-list__section',
            'page-list__section--archived-list'
          ]"
        />

      </div>

    </transition>

    <!-- SIDEBAR: new remote work request -->
    <ui-sidebar
      :id="TRANSAT_NEW_REMOTE_WORK_MODAL_NAME"
      v-model="showRemoteWorkCreationSidebar"
      placement="end"
      shadow
      no-close-on-esc
      lazy
      fit-workspace
    >
      <template #title>
        <ui-wad class="d-flex gap-1 align-items-center">
          <ui-wad class="font-family-heading-jumbo-light font-size-24px">
            Ajout d'une nouvelle demande
          </ui-wad>
          <ui-badge-heavy
            :class="[
              'page-list__badge',
              `page-list__badge--remote-work`,
              'pt-1 pr-1'
            ]"
          >
            <span class="font-size-90">TT</span>
            <span class="page-list__badge__sub">Télétravail</span>
          </ui-badge-heavy>
        </ui-wad>
      </template>

      <remote-work-create
        v-bind="{
          singleScheduleRecordCallback,
          submitSingleCallback: singleCreationCallback,
          serviceList,
        }"
      />
    </ui-sidebar>

  </div>
</template>

<style lang="sass" scoped>
@import ~/assets/sass/modules/layout/variables
@import ~/assets/sass/modules/transat/members

.page-list
  $self: &

  padding-top: $layout-spacer-y * .25
  padding-left: $layout-spacer-x
  padding-right: $layout-spacer-x
  padding-bottom: $layout-spacer-y

  &__filters
    margin-bottom: $layout-spacer-y * 2
    max-width: map-get($breakpoints, 'laptop')

  &__body
    // empirical
    // (due to a lot of data here, could not rely on `$breakpoints`)
    $min-width: 1240px

    min-width: $min-width

  &__section
    margin-top: $layout-spacer-y * 1.5

  &__badge
    @include leave-request-type-badge

    // (empirical:  fitting <ui-form-tree size="sm">)
    $padding-y: 4px

    border-radius: initial // seamlessly embed the badge into the filter item
    padding-top: $padding-y
    padding-bottom: $padding-y

    &__sub
      font-size: 80%
      text-transform: lowercase
      font-weight: $font-weight-normal
      vertical-align: bottom
      margin-left: space-x(1)

</style>

<style lang="sass">
@import '~/assets/sass/modules/layout/members'
@import '~/assets/sass/modules/transat/variables'

$simulated-header-height: 54px
$top-teaser-height: 16px
$background-color: $body-bg

// @XXX Vue3 lack of State-Driven Dynamic CSS API
#transat-remote-work-shortcut-creation-sidebar
  width: $create-sidebar-size
  background-color: $background-color

  .offcanvas-body

    &::after
      $offset-x: calc( var(--bs-offcanvas-padding-x) * -1 )

      @include virtual-content
      @include no-interaction
      @include top-teaser($background-color)

      position: fixed
      width: 100%
      height: $top-teaser-height
      top: $simulated-header-height // (quick & dirty teaser integration)
      transform: translateX($offset-x)

  @include media('<#{$create-sidebar-size}')
    @include fit-sidebar($create-sidebar-size)

</style>

<script lang="ts">
import PendingList, { type DefaultData as PendingListDefaultData} from '~/components/transat/remote-work/list/PendingList.vue'
import ArchivedList, { type DefaultData as ArchivedListDefaultData } from '~/components/transat/remote-work/list/ArchivedList.vue'
import RemoteWorkFiltersComponent from '~/components/transat/remote-work/list/Filters.vue'
import RemoteWorkCreate from '~/components/transat/remote-work/create/Organism.vue'
import Console from '~/utils/lib/core/console'
import { clone, map, merge, tapCatch, toString } from '~/utils/lib/core/prelude'
import { MENU_ITEM, NAVIGATION_SCOPE } from '~/utils/lib/layout/types'
import { useLegacyPageFetch } from '~/composables/core/hook'
import { useToastBuilder } from '~/composables/layout/toast'
import { useCompanyRequiredNavigationContext } from '~/composables/layout/navigation'
import { useAccessControl } from '~/composables/layout/access-control'
import { useStore_transat_service_list } from '~/stores/transat/service/list'
import type { Service_ } from '~/stores/transat/service'
import { useStore_transat_parameters_module, type LeaveModuleInterface } from '~/stores/transat/parameters/module'
import { anyFilterSet, remoteWorkBoilerplate, useStore_transat_remoteWork_list, type RemoteWorkFilters } from '~/stores/transat/remote-work/list'
import { INITIAL_HYDRATION, type Context } from '~/composables/layout/table-provider'
import { wrap } from '~/utils/infra/common/types'
import type { PERIOD } from '~/stores/transat/remote-work';
import { STATUS, EVENTS } from '~/stores/transat/remote-work'
import { useStore_transat_remoteWork_create } from '~/stores/transat/remote-work/create'
import { useStore_transat_remoteWork_scheduling } from '~/stores/transat/remote-work/scheduling'
import { useStore_transat_layout } from '~/stores/transat/layout'

const TRANSAT_NEW_REMOTE_WORK_MODAL_NAME = 'transat-remote-work-shortcut-creation-sidebar'

const console = new Console('component:remote-work-list')

export default defineComponent({
  components: { RemoteWorkFiltersComponent, PendingList, ArchivedList, RemoteWorkCreate },
  provide() {
    return {
      [EVENTS.ON_PENDING_LIST_REFRESH_NEEDED]:
        (muteBusyState) => this.refreshPendingList(muteBusyState),
      [EVENTS.ON_ARCHIVED_LIST_REFRESH_NEEDED]:
        (muteBusyState) => this.refreshArchivedList(muteBusyState),
    }
  },
  setup() {
    definePageMeta({
      navigationScope: NAVIGATION_SCOPE.COMPANY_REQUIRED,
      menuItem: MENU_ITEM.ABSENCES,
    })
    const navigationContext = useCompanyRequiredNavigationContext()

    useHead({
      title: `Télétravail — ${navigationContext.fromCompanyLevel().company.raison_sociale}`,
    })

    const {
      fetchState,
      fetchWrapper,
    } = useLegacyPageFetch()

    const ToastBuilder = useToastBuilder()

    const ref_pendingList = useTemplateRef<InstanceType<typeof PendingList>>('pendingList')
    const ref_archivedList = useTemplateRef<InstanceType<typeof ArchivedList>>('archivedList')

    const store_transat_layout = useStore_transat_layout()
    const store_service_list = useStore_transat_service_list()
    const store_parameters_module = useStore_transat_parameters_module()
    const store_remoteWork_list = useStore_transat_remoteWork_list()
    const store_remoteWork_create = useStore_transat_remoteWork_create()
    const store_remoteWork_scheduling = useStore_transat_remoteWork_scheduling()

    store_transat_layout.setNavSection('list_remote_work')

    return {
      ...navigationContext,
      ...useAccessControl(),
      fetchState,
      fetchWrapper,
      ToastBuilder,
      store_service_list,
      store_parameters_module,
      store_remoteWork_list,
      store_remoteWork_create,
      store_remoteWork_scheduling,
      ref_pendingList,
      ref_archivedList,
    }
  },
  data() {
    const remoteWorkFilters = clone(remoteWorkBoilerplate)

    return {
      remoteWorkFilters,
      defaultPendingData: null as unknown as PendingListDefaultData, // (lazy)
      defaultArchivedData: null as unknown as ArchivedListDefaultData, // (lazy)
      parametersModule: null as unknown as LeaveModuleInterface, // (lazy)
      serviceList: [] as Service_[], // (lazy)
      withPendingBusyState: true,
      withArchivedBusyState: true,
      TRANSAT_NEW_REMOTE_WORK_MODAL_NAME,
      showRemoteWorkCreationSidebar: false,
    }
  },
  computed: {
    anyRemoteWorkFilterSet() {
      return anyFilterSet(this.remoteWorkFilters)
    },
  },
  created() {
    this.fetchWrapper(
      () => {
        const options = {
          uuidData: this.fromCompanyLevel().structure.uuidData,
          code_societe: this.fromCompanyLevel().company.code_societe
        }

        const defaultPendingContext :Partial<Context> = {
          perPage: 20,
          currentPage: 1,
          order: 'from',
          sort: 'asc',
        }

        const defaultArchivedContext :Partial<Context> = {
          perPage: 20,
          currentPage: 1,
          order: 'from',
          sort: 'desc',
        }

        return Promise.all([
            this.searchPendingCallback(defaultPendingContext),
            this.searchArchivedCallback(defaultArchivedContext),
            this.store_service_list.readEntire(options),
            this.store_parameters_module.read(options),
          ])
          .then(results => {

            this.defaultPendingData = {
              initialHydration: INITIAL_HYDRATION.EAGER_INJECTION,
              currentItems: results[0].data,
              context: merge(
                defaultPendingContext,
                results[0].context
              )
            }

            this.defaultArchivedData = {
              initialHydration: INITIAL_HYDRATION.EAGER_INJECTION,
              currentItems: results[1].data,
              context: merge(
                defaultArchivedContext,
                results[1].context
              )
            }

            this.serviceList = results[2]

            this.parametersModule = results[3]
          })
          .catch(tapCatch((err) => {
            console.warn('fetch', err)

            this.ToastBuilder
              .error(toString(err))
              .title(`Demande de télétravail`)
              .show()
          }))
      }
    )
  },
  methods: {
    updateRemoteWorkFiltersCallback(
      remoteWorkFilters: RemoteWorkFilters
    ) {
      this.remoteWorkFilters = remoteWorkFilters

      const muteBusyState = false

      this.refreshPendingList(muteBusyState)
      this.refreshArchivedList(muteBusyState)
    },
    singleScheduleRecordCallback({ matricule, fromDate, toDate }) {
      const options = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        dateMin: fromDate,
        dateMax: toDate,
        matricule,
      }

      return this.store_remoteWork_scheduling.calendar(options)
        .catch(tapCatch(err => {
          console.warn('singleScheduleRecordCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Récupération des congés d'un utilisateur`)
            .show()
        }))
    },
    singleCreationCallback(data: { matricule: string, datesMap: Record<string, PERIOD>, comment: string }) {
      const options = {
        ...data,
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
      }

      return this.store_remoteWork_create.create(options)
        .then(() => {
          this.showRemoteWorkCreationSidebar = false

          const muteBusyState = true
          this.refreshArchivedList(muteBusyState)

          this.ToastBuilder
            .success()
            .title(`Demande de télétravail ajoutée et validée`)
            .show()
        })
        .catch(tapCatch(err => {
          console.warn('singleCreationCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Ajout d'une demande de télétravail`)
            .show()
        }))
    },
    refreshPendingList(muteBusyState = false) {
      if (muteBusyState)
        this.withPendingBusyState = false

      this.ref_pendingList?.refreshTable()
    },
    refreshArchivedList(muteBusyState = false) {
      if (muteBusyState)
        this.withArchivedBusyState = false

      this.ref_archivedList?.refreshTable()
    },
    searchPendingCallback(context) {
      const input = {
        ...wrap(context),
        filters: {
          ...clone(this.remoteWorkFilters),
          statusList: [STATUS.WAITING]
        },
        order: 'date',
        sort: 'asc' as const
      }

      const options = {
        input,
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
      }

      return this.store_remoteWork_list.read(options)
        .catch(tapCatch(err => {
          console.warn('searchPendingCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Recherche de demandes en attente`)
            .show()
        }))
        .finally(() => this.withPendingBusyState = true)
    },
    searchArchivedCallback(context) {
      const input = {
        ...wrap(context),
        filters: {
          ...clone(this.remoteWorkFilters),
          statusList: [
            STATUS.ACCEPTED
          ]
        },
        order: 'date',
        sort: 'desc' as const
      }

      const options = {
        input,
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
      }

      return this.store_remoteWork_list.read(options)
        .catch(tapCatch(err => {
          console.warn('searchArchivedCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Recherche de demandes archivées`)
            .show()
        }))
        .finally(() => this.withArchivedBusyState = true)
    },
    approveCallback(grouped_request_id) {
      const options = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        grouped_request_id,
        action: 'accept' as const,
      }

      return this.store_remoteWork_list.update(options)
        .then(() => {
          const muteBusyState = true
          this.refreshArchivedList(muteBusyState)
        })
        .catch(tapCatch(err => {
            console.warn('approveCallback', err)
            this.ToastBuilder
              .error(toString(err))
              .title(`Validation d'une demande de télétravail`)
              .show()
          }))
    },
    approveMultipleCallback(grouped_request_ids: string[]) {
      const options = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        action: 'accept' as const,
      }

      return Promise.all(
        map.iterable(
          (grouped_request_id) => this.store_remoteWork_list.update({ ...options, grouped_request_id }),
          grouped_request_ids
        )
      )
      .then(() => {
        const muteBusyState = true
        this.refreshArchivedList(muteBusyState)
      })
      .catch(tapCatch(err => {
        console.warn('approveMultipleCallback', err)
        this.ToastBuilder
          .error(toString(err))
          .title(`Validation groupée de demandes de télétravail`)
          .show()
      }))
    },
    rejectCallback(grouped_request_id, reason) {
      const options = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        grouped_request_id,
        reason,
        action: 'reject' as const,
      }

      return this.store_remoteWork_list.update(options)
        .then(() => {
          const muteBusyState = true
          this.refreshArchivedList(muteBusyState)
        })
        .catch(tapCatch(err => {
          console.warn('rejectCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Refus d'une demande de télétravail`)
            .show()
        }))
    },
    cancelCallback(grouped_request_id, reason) {
      const options = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        grouped_request_id,
        reason,
        action: 'cancel' as const,
      }

      return this.store_remoteWork_list.update(options)
        .catch(tapCatch(err => {
          console.warn('cancelCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Annulation d'une demande de télétravail`)
            .show()
        }))
    },
    singleAcceptCallback(request_id: string) {
      const options = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        request_id,
        action: 'accept' as const,
      }

      return this.store_remoteWork_list.updateSingle(options)
        .then(() => {
          const muteBusyState = false
          this.refreshPendingList(muteBusyState)
          this.refreshArchivedList(muteBusyState)
        })
        .catch(tapCatch(err => {
          console.warn('singleAcceptCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Validation d'une demande de télétravail`)
            .show()
        }))
    },
    singleRejectCallback(request_id: string, reason: string) {
      const options = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        request_id,
        reason,
        action: 'reject' as const,
      }

      return this.store_remoteWork_list.updateSingle(options)
        .then(() => {
          const muteBusyState = false
          this.refreshPendingList(muteBusyState)
          this.refreshArchivedList(muteBusyState)
        })
        .catch(tapCatch(err => {
          console.warn('singleRejectCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Refus d'une demande de télétravail`)
            .show()
        }))
    },
    singleCancelCallback(request_id: string, reason: string) {
      const options = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        request_id,
        reason,
        action: 'cancel' as const,
      }

      return this.store_remoteWork_list.updateSingle(options)
        .then(() => {
          const muteBusyState = false
          this.refreshPendingList(muteBusyState)
          this.refreshArchivedList(muteBusyState)
        })
        .catch(tapCatch(err => {
          console.warn('singleCancelCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Annulation d'une demande de télétravail`)
            .show()
        }))
    },
  }
})
</script>
